import { useMemo, useState } from 'react';
import {
  useGetLecturerSummaryQuery,
  useGetLecturerPerformanceQuery,
  type LecturerPerformanceParams,
} from '@/entities/Dashboard';
import { CustomInput } from '@/shared/ui/Fields';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import SearchIcon from '@/shared/assets/icons/search.svg';
import UsersIcon from '@/shared/assets/icons/dashboard/Users.svg';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import { buildMetric } from '../../../lib/metrics';
import { formatGrade, formatPercentOrDash, formatFullName } from '../../../lib/rows';
import {
  DASHBOARD_DATE_PERIOD_OPTIONS,
  toDashboardDateParam,
  type DashboardDatePeriod,
} from '../../../lib/datePeriod';
import type { MetricItem } from '../../../config/mockDashboardData';
import { AnalyticsMetricsGrid } from '../../components/AnalyticsMetricCard';
import { AnalyticsSectionHeader } from '../../components/AnalyticsSectionHeader';
import { DashboardFilters, type DashboardFilterValue } from '../../components/DashboardFilters';
import { AnalyticsPagination } from '../../components/AnalyticsTable';
import styles from '../../dashboard/Dashboard.module.scss';

const PAGE_SIZE = 8;

interface LecturerStatisticsTabProps {
  onExport: () => void;
}

export const LecturerStatisticsTab = ({ onExport }: LecturerStatisticsTabProps) => {
  const [datePeriod, setDatePeriod] = useState<DashboardDatePeriod>('this-month');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<DashboardFilterValue>({});
  const debouncedSearch = useDebounce(search.trim(), 500);
  const date = toDashboardDateParam(datePeriod);

  const { data: summary } = useGetLecturerSummaryQuery({ date });

  const performanceParams = useMemo<LecturerPerformanceParams>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch || undefined,
      courseType: filters.type,
      courseId: filters.courseId,
      intakeId: filters.intakeId,
      termId: filters.termId,
      groupId: filters.groupId,
    }),
    [page, debouncedSearch, filters],
  );

  const { data: performance } = useGetLecturerPerformanceQuery(performanceParams);

  const metrics = useMemo<MetricItem[]>(() => {
    const s = summary?.data;
    return [
      buildMetric('1', 'Lecturers', s?.lecturersCount, 'count'),
      buildMetric('2', 'Modules per Lecturer', s?.modulesPerLecturer, 'count'),
      buildMetric('3', 'Students per Lecturer', s?.studentsPerLecturer, 'count'),
    ];
  }, [summary]);

  const rows = performance?.data?.lecturers ?? [];
  const pagination = performance?.data?.pagination;
  const total = pagination?.total ?? 0;

  const handleFilters = (value: DashboardFilterValue) => {
    setFilters(value);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className={styles.root}>
      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Lecturer Analytics"
          icon={<Svg Icon={UsersIcon} width={20} height={20} className="text-[#1E7E34]" />}
          iconBg="#E8F8EE"
          onExport={onExport}
          periodOptions={DASHBOARD_DATE_PERIOD_OPTIONS}
          periodValue={datePeriod}
          onPeriodChange={(key) => setDatePeriod(key as DashboardDatePeriod)}
        />
        <AnalyticsMetricsGrid metrics={metrics} columns={3} />
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader
          title={`Lecturer Performance (${total} lecturers)`}
          icon={<Svg Icon={UsersIcon} width={20} height={20} className="text-[#0068D6]" />}
          iconBg="#E8F0FF"
          showToolbar={false}
        />
        <CustomInput aria-label="Search"
          className={searchInputClassName}
          startContent={<Svg Icon={SearchIcon} />}
          placeholder="Search"
          value={search}
          onValueChange={handleSearch}
        />
        <DashboardFilters value={filters} onChange={handleFilters} showTerm twoRow />
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className="sr-only">Lecturer performance</caption>
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Name</th>
                <th scope="col">Teaching Hours / Week</th>
                <th scope="col">Active Modules</th>
                <th scope="col">Avg. Modules Grade</th>
                <th scope="col">Avg. Student Progress</th>
                <th scope="col">Avg. Module Completion Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user?.id ?? row.user?.username}>
                  <td>{row.user?.id ?? '—'}</td>
                  <td>{formatFullName(row.user)}</td>
                  <td className={row.teachingHoursPerWeek === 0 ? styles.atRisk : undefined}>
                    {row.teachingHoursPerWeek}
                  </td>
                  <td className={row.modulesCount === 0 ? styles.atRisk : undefined}>
                    {row.modulesCount}
                  </td>
                  <td>{formatGrade(row.avgModulesGrade)}</td>
                  <td>
                    <div className={styles.progressCell}>
                      <span>{formatPercentOrDash(row.avgStudentProgressByLessons)} lessons</span>
                      <span className={styles.progressSub}>
                        {formatPercentOrDash(row.avgStudentProgressByModules)} modules
                      </span>
                    </div>
                  </td>
                  <td>{formatPercentOrDash(row.avgModuleCompletionRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AnalyticsPagination
          page={pagination?.page ?? page}
          pageCount={pagination?.pageCount ?? 0}
          onPageChange={setPage}
        />
      </section>
    </div>
  );
};
